/**
 * PATH: apps/hr/src/pages/documents/documents-list/useDocumentsData.ts
 * PURPOSE: Custom hook encapsulating document fetching, mapping, filtering, and pagination
 * WHY: Extracts data logic from DocumentsPage to keep it under 300 lines
 */

import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hrApi } from '../../../lib/setupApi'
import type { Document as DocType, DocumentStatus, DocumentType, Employee } from '../../../types'

function mapTicketTypeToDocumentType(ticketType: string): DocumentType {
  const typeMap: Record<string, DocumentType> = {
    'Driving License': 'license',
    'White Card': 'license',
    'EWP License': 'license',
    'Forklift License': 'license',
    'First Aid Certificate': 'certification',
    'Certificate': 'certification',
    'Passport': 'identification',
    'Working With Children Check': 'certification',
    'Police Check': 'certification',
    'Trade Certificate': 'certification',
    'Visa': 'visa',
    'Medical': 'medical',
    'Training': 'training',
  }
  return typeMap[ticketType] || 'other'
}

export function useDocumentsData() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Filter states
  const [statusFilter, setStatusFilter] = useState<'all' | DocumentStatus>(
    (searchParams.get('status') as DocumentStatus) || 'all'
  )
  const [typeFilter, setTypeFilter] = useState<'all' | DocumentType>(
    (searchParams.get('type') as DocumentType) || 'all'
  )
  const [expiryFilter, setExpiryFilter] = useState(searchParams.get('expiry') || '')
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10))
  const pageSize = 20

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Fetch documents
  const { data: apiResponse, isLoading, refetch } = useQuery({
    queryKey: ['all-documents', statusFilter, typeFilter, debouncedSearch, currentPage],
    queryFn: async () => {
      try {
        const response = await hrApi.getAllDocuments({
          status_filter: statusFilter !== 'all' ? statusFilter : undefined,
          search: debouncedSearch || undefined,
          page: currentPage,
          page_size: pageSize,
        })
        return response.data
      } catch (error) {
        console.error('[HR] Failed to fetch documents:', error)
        return null
      }
    },
  })

  // Fetch employees for upload modal
  const { data: employeesData } = useQuery({
    queryKey: ['employees-list'],
    queryFn: async () => {
      try {
        const response = await hrApi.getEmployees()
        return response.data?.workers || []
      } catch (error) {
        console.error('[HR] Failed to fetch employees:', error)
        return []
      }
    },
  })

  // Calculate stats from documents
  const stats = useMemo(() => {
    const docs = apiResponse?.data?.documents || []
    return {
      total: docs.length,
      expiring30Days: docs.filter((d: any) => d.days_until_expiry !== undefined && d.days_until_expiry >= 0 && d.days_until_expiry <= 30).length,
      expired: docs.filter((d: any) => d.days_until_expiry !== undefined && d.days_until_expiry < 0).length,
      needsReview: docs.filter((d: any) => d.document_status === 'needs_review').length,
      pendingOCR: docs.filter((d: any) => d.document_status === 'pending_ocr').length,
    }
  }, [apiResponse])

  // Map API documents to frontend types
  const documents: DocType[] = useMemo(() => {
    if (!apiResponse?.data?.documents) return []
    return apiResponse.data.documents.map((doc: any) => ({
      id: doc.id,
      documentNumber: doc.ticket_number || '',
      documentType: mapTicketTypeToDocumentType(doc.ticket_type),
      documentName: doc.document_name || doc.ticket_type,
      employeeId: doc.worker_id,
      employeeName: doc.employee_name,
      status: (doc.document_status as DocumentStatus) || 'pending_ocr',
      issueDate: doc.issued_date,
      expiryDate: doc.expiry_date,
      daysUntilExpiry: doc.days_until_expiry,
      confidenceScore: doc.ocr_confidence ? Math.round(doc.ocr_confidence * 100) : undefined,
      tags: doc.tags || [],
      fileUrl: doc.file_url || '',
      fileName: doc.file_name || '',
      fileType: doc.file_type || '',
      fileSize: doc.file_size,
      issuer: doc.issuer,
      notes: doc.notes,
      createdAt: doc.created_at || '',
      updatedAt: doc.updated_at || '',
    }))
  }, [apiResponse])

  // Map employees
  const employees: Employee[] = useMemo(() => {
    if (!employeesData) return []
    return employeesData.map((w: any) => ({
      id: w.id,
      firstName: w.first_name,
      lastName: w.last_name,
      email: w.email || '',
      status: 'active' as const,
    }))
  }, [employeesData])

  const pagination = apiResponse?.data?.pagination || { total: 0, total_pages: 1 }

  // Client-side expiry filtering
  const filteredDocuments = useMemo(() => {
    if (!expiryFilter) return documents
    return documents.filter(d => {
      if (!d.daysUntilExpiry) return false
      if (expiryFilter === 'expired') return d.daysUntilExpiry <= 0
      if (expiryFilter === '7days') return d.daysUntilExpiry > 0 && d.daysUntilExpiry <= 7
      if (expiryFilter === '30days') return d.daysUntilExpiry > 0 && d.daysUntilExpiry <= 30
      if (expiryFilter === '60days') return d.daysUntilExpiry > 0 && d.daysUntilExpiry <= 60
      return true
    })
  }, [documents, expiryFilter])

  // Sync URL params
  useEffect(() => {
    const uploadParam = searchParams.get('upload')
    if (uploadParam === 'true') {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('upload')
      setSearchParams(newParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    const params = new URLSearchParams()
    if (currentPage > 1) params.set('page', currentPage.toString())
    if (search) params.set('search', search)
    if (statusFilter !== 'all') params.set('status', statusFilter)
    if (typeFilter !== 'all') params.set('type', typeFilter)
    if (expiryFilter) params.set('expiry', expiryFilter)
    setSearchParams(params, { replace: true })
  }, [currentPage, search, statusFilter, typeFilter, expiryFilter, setSearchParams])

  const handleClearFilters = () => {
    setSearch(''); setStatusFilter('all'); setTypeFilter('all'); setExpiryFilter(''); setCurrentPage(1)
  }

  const hasActiveFilters = !!(search || statusFilter !== 'all' || typeFilter !== 'all' || expiryFilter)
  const shouldShowUploadOnMount = searchParams.get('upload') === 'true'

  return {
    stats, filteredDocuments, employees, isLoading, pagination,
    statusFilter, setStatusFilter, typeFilter, setTypeFilter,
    expiryFilter, setExpiryFilter, search, setSearch,
    showFilters, setShowFilters, currentPage, setCurrentPage,
    hasActiveFilters, handleClearFilters, refetch, queryClient,
    shouldShowUploadOnMount,
  }
}
